import { ApiErrorCode, withApiErrorHandler } from '@/modules/common/api/errors';
import { axiosInstance } from '@/utils/axios';

import { mapSenioritiesFromDTO } from '../mappers/seniority.mapper';
import {
  DeleteSeniorityRequest,
  UpsertSenioritiesRequest,
  UpsertSeniorityRequestItem,
} from '../types/request/seniority-request';
import { SeniorityWithMemberCountResponse } from '../types/response/seniority-with-member-count-response';
import { Seniority } from '../types/seniority';

/**
 * Fetches all seniorities for the organization
 * Returns complete list in single request (no pagination)
 */
export const getSeniorities = async (): Promise<Seniority[]> => {
  const endpoint = '/api/seniorities';

  const response = await withApiErrorHandler(endpoint, ApiErrorCode.GENERIC_ERROR, () =>
    axiosInstance.get<{ data: SeniorityWithMemberCountResponse[] }>(endpoint)
  );

  return mapSenioritiesFromDTO(response);
};

/**
 * Updates seniorities in bulk (create new, update existing)
 * Sends only seniorities with valid names
 */
export const updateSeniorities = async (
  seniorities: UpsertSeniorityRequestItem[]
): Promise<Seniority[]> => {
  const endpoint = '/api/seniorities';

  // Filter out new seniorities (negative IDs) and include only those with names
  const senioritiesToUpdate = seniorities
    .filter(sen => sen.id && sen.id > 0)
    .map(sen => ({
      id: sen.id,
      name: sen.name,
    }));

  // Include new seniorities (without ID)
  const newSeniorities = seniorities
    .filter(sen => !sen.id || sen.id < 0)
    .map(sen => ({
      name: sen.name,
    }));

  const requestBody: UpsertSenioritiesRequest = {
    seniorities: [...senioritiesToUpdate, ...newSeniorities],
  };

  const response = await withApiErrorHandler(endpoint, ApiErrorCode.GENERIC_ERROR, () =>
    axiosInstance.patch<{ data: SeniorityWithMemberCountResponse[] }>(endpoint, requestBody)
  );

  return mapSenioritiesFromDTO(response);
};

/**
 * Deletes a seniority and optionally transfers members to another seniority
 */
export const deleteSeniority = async ({
  seniorityId,
  replacementSeniorityId,
}: {
  seniorityId: number;
  replacementSeniorityId?: number;
}): Promise<void> => {
  const endpoint = '/api/seniorities';

  const requestBody: DeleteSeniorityRequest = {
    seniorityId,
    ...(replacementSeniorityId !== undefined && { replacementSeniorityId }),
  };

  await withApiErrorHandler(endpoint, ApiErrorCode.GENERIC_ERROR, () =>
    axiosInstance.delete(endpoint, { data: requestBody })
  );
};
